import React, { useEffect, useState, useRef } from 'react'
import { Card, CardContent, Typography, TextField, Grid, Box, Button } from '@mui/material';
import PageHeader2 from '../../../componentsII/PageHeader2';
import { CircleArrowUp, Printer, Download } from 'lucide-react';
import api from '../../../axios/api';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import html2pdf from 'html2pdf.js';
import { showNotification } from "../../../redux/slices/notificationSlice";
import Loader from '../../../componentsII/Loader';
import CustomButton from '../../../componentsII/Buttons';
import boiler from '../../../images/NoData.jpg';

// Helper function to format the scope 3 category names
const formatScope3CategoryName = (source) => {
  if (source.startsWith("scope_3_cat_")) {
    return source.replace("scope_3_cat_", "");
  }
  return source;
};

// Helper function to map scope 1 source names to readable titles
const getScope1Title = (source) => {
  const titleMap = {
    'equip_fuel': 'Equipment Fuel',
    'equip_refrigerant': 'Fugitive Emissions - Refrigerants',
    'vehicle_fuel': 'Mobile Combustion - Vehicle Fuel',
    'vehicle_distance': 'Mobile Combustion - Vehicle Distance',
    'loc_nat_gas': 'Stationary Combustion - Facility',
    'equip_ind_gas': 'Fugitive Emissions - Industrial Gases'
  };
  return titleMap[source] || source;
};

// Helper function to map scope 2 source names to readable titles
const getScope2Title = (source) => {
  const titleMap = {
    'loc_electricity': 'Purchased Electricity - Facility',
    'loc_heat_steem': 'Purchased Heat & Steam',
    'loc_cooling': 'Purchased Cooling'
  };
  return titleMap[source] || source;
};

// Helper function to get month name from number (0-11)
const getMonthName = (monthNumber) => {
  return dayjs().month(monthNumber).format('MMMM');
};

const EmissionSection = ({ title, emissions }) => {
  return (
    <div className="space-y-4 p-5">
      <h3 className="text-lg font-medium">{title}</h3>
      <div className="space-y-4 pl-4">
        <div>
          <label className="block text-sm font-medium text-gray-600">GHG Calculation Methodology</label>
          <TextField
            type="text"
            size="small"
            disabled
            className="mt-1 w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-600">Calculation Type</label>
          <TextField
            type="text"
            size="small"
            disabled
            className="mt-1 w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-600">Emission Factor Set</label>
          <TextField
            type="text"
            size="small"
            disabled
            className="mt-1 w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-600">Emissions (CO₂ Tonnes)</label>
          <TextField
            type="text"
            value={emissions !== null ? emissions.toFixed(6) : '--'}
            size="small"
            disabled
            className="mt-1 w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>
    </div>
  );
};

const ReportingPeriodForm = ({ reportData }) => {
  const startMonth = reportData?.start_year_month !== undefined ? getMonthName(reportData.start_year_month) : '';
  const endMonth = reportData?.end_year_month !== undefined ? getMonthName(reportData.end_year_month) : '';
  const startYear = reportData?.start_year || '';
  const endYear = reportData?.end_year || '';

  // Format the date range
  const dateRange = (startMonth && startYear && endMonth && endYear)
    ? `${startMonth} ${startYear} - ${endMonth} ${endYear}`
    : '';

  return (
    <Card className="w-full max-w-2xl" sx={{
      backgroundColor: '#f2f2f2',
    }}>
      <CardContent className="space-y-6">
        <Typography variant="h5" fontWeight="bold" className='pt-5'>
          Reporting Period
        </Typography>
        <Typography variant="body" className='pt-5'>The period of time for which the data is collected.</Typography>
        <div className="space-y-4 pl-4">
          <div>
            <label className="block text-sm font-medium text-gray-600">Reporting Year</label>
            <TextField
              type="text"
              value={dateRange}
              size="small"
              disabled
              className="mt-1 w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const Scope1Form = ({ data }) => {
  return (
    <Card className="w-full max-w-2xl mt-5" sx={{
      backgroundColor: '#f2f2f2',
    }}>
      <CardContent className="space-y-6">
        <Typography variant="h5" fontWeight="bold" className='pt-5'>
          Scope 1
        </Typography>
        <Typography variant="body" className='pt-5'>Direct GHG emissions</Typography>

        {data && data.length > 0 ? (
          data.map((item, index) => (
            <EmissionSection
              key={index}
              title={getScope1Title(item.source)}
              emissions={item.co2_emission_tones}
            />
          ))
        ) : (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <img
              src={boiler}
              alt="No Data"
              className="w-52 h-52 mx-auto text-gray-400 mb-4"
            />
            <h3 className="text-xl font-medium text-gray-600 mb-2">
              No Scope 1 Emissions Data Found
            </h3>
            <p className="text-gray-500">
              Add your Scope 1 emission sources to get started
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const Scope2Form = ({ data }) => {
  return (
    <Card className="w-full max-w-2xl mt-5" sx={{
      backgroundColor: '#f2f2f2',
    }}>
      <CardContent className="space-y-6">
        <Typography variant="h5" fontWeight="bold" className='pt-5'>
          Scope 2
        </Typography>
        <Typography variant="body" className='pt-5'>Location/Market Based GHG Emissions</Typography>

        {data && data.length > 0 ? (
          data.map((item, index) => (
            <EmissionSection
              key={index}
              title={getScope2Title(item.source)}
              emissions={item.co2_emission_tones}
            />
          ))
        ) : (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <img
              src={boiler}
              alt="No Data"
              className="w-52 h-52 mx-auto text-gray-400 mb-4"
            />
            <h3 className="text-xl font-medium text-gray-600 mb-2">
              No Scope 2 Emissions Data Found
            </h3>
            <p className="text-gray-500">
              Add your Scope 2 emission sources to get started
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const Scope3Form = ({ data }) => {
  // Filter out null emission categories
  const validCategories = data ? data.filter(item => item.co2_emission_tones !== null) : [];

  return (
    <Card className="w-full max-w-2xl mt-5" sx={{
      backgroundColor: '#f2f2f2',
    }}>
      <CardContent className="space-y-6">
        <Typography variant="h5" fontWeight="bold" className='pt-5'>
          Scope 3
        </Typography>
        <Typography variant="body" className='pt-5'>Other Indirect GHG Emissions</Typography>

        {data && data.length > 0 ? (
          data.map((item, index) => (
            <EmissionSection
              key={index}
              title={formatScope3CategoryName(item.source)}
              emissions={item.co2_emission_tones}
            />
          ))
        ) : (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <img
              src={boiler}
              alt="No Data"
              className="w-52 h-52 mx-auto text-gray-400 mb-4"
            />
            <h3 className="text-xl font-medium text-gray-600 mb-2">
              No Scope 3 Emissions Data Found
            </h3>
            <p className="text-gray-500">
              Add your Scope 3 emission sources to get started
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Report footer component for displaying the current date
const ReportFooter = () => {
  const currentDate = dayjs().format('MMMM D, YYYY');
  
  return (
    <div className="report-footer w-full max-w-2xl mt-8 text-right text-gray-500 text-sm">
      <p>Report generated on: {currentDate}</p>
    </div>
  );
};

const GHGMethodologyReport = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const year = useSelector((state) => state.user.selectedYear);
  const reportRef = useRef(null);

  // const handleDownloadPDF = () => {
  //   if (!reportRef.current) return;

  //   const element = reportRef.current;
  //   const opt = {
  //     margin: 10,
  //     filename: `GHG_Methodology_Report_${year?.year || 'current'}.pdf`,
  //     image: { type: 'jpeg', quality: 0.98 },
  //     html2canvas: { scale: 2, useCORS: true },
  //     jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
  //   };

  //   html2pdf().set(opt).from(element).save();
  // };

  // Function to print the report
  
  const handleDownloadPDF = () => {
  if (!reportRef.current) return;

  const element = reportRef.current;

  // Apply temporary scale-down transform to prevent cutting
  element.style.transform = 'scale(0.95)';
  element.style.transformOrigin = 'top left';

  const opt = {
    margin: [10, 10, 10, 10], // top, left, bottom, right in mm
    filename: `GHG_Methodology_Report_${year?.year || 'current'}.pdf`,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: {
      scale: 1.2,         // Downscale to reduce overflow
      useCORS: true,
      allowTaint: true,
      scrollY: 0,
    },
    jsPDF: {
      unit: 'mm',
      format: 'a4',
      orientation: 'portrait',
    },
    pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
  };

  html2pdf().set(opt).from(element).save().then(() => {
    // Restore original transform
    element.style.transform = '';
  });
};


  const handlePrint = () => {
    const printContent = reportRef.current;
    if (!printContent) return;

    const originalContents = document.body.innerHTML;
    document.body.innerHTML = printContent.innerHTML;

    window.print();

    // Restore original content after printing
    document.body.innerHTML = originalContents;

    // Re-render the component to restore functionality
    window.location.reload();
  };

  useEffect(() => {
    const fetchReportData = async () => {
      try {
        setLoading(true);
        setError(null); // Clear any previous errors
        
        // Fetch data from the API
        const res = await api.get(`/reports/getGHGMethodologyReport?comp_data_year_id=${year.id}`);
        
        // Check if we have actual data or an empty response
        if (res.data) {
          setReportData(res.data);
        } else {
          // If response is successful but no data, set reportData to empty object with empty arrays
          setReportData({ scope_1: [], scope_2: [], scope_3: [] });
        }
        
        setLoading(false);
      } catch (e) {
        console.error("Error fetching GHG methodology report:", e);
        // Only show error notification for actual errors, not for empty data
        if (e.response && e.response.status === 404) {
          // 404 means no data found - don't treat as error
          setReportData({ scope_1: [], scope_2: [], scope_3: [] });
          setError(null);
        } else {
          // Real error occurred
          setError("Failed to load report data");
          showNotification({ message: "Failed to load report data", type: "error" });
        }
        setLoading(false);
      }
    };

    if (year && year.id) {
      fetchReportData();
    } else {
      // No year selected, clear data and set loading to false
      setReportData(null);
      setLoading(false);
    }
  }, [year]);

  // Check if there's truly no data available for display
  const noDataAvailable = !loading && !error && (
    !reportData || 
    ((!reportData.scope_1 || reportData.scope_1.length === 0) && 
     (!reportData.scope_2 || reportData.scope_2.length === 0) && 
     (!reportData.scope_3 || reportData.scope_3.length === 0))
  );

  return (
    <div>
      <PageHeader2
        title="GHG Methodology Report"
        text={[
          "View and export your footprint methodology report to better understand the",
          "methodologies and emission factor sets used to calculate your footprint."
        ]}
        buttonIcon={<CircleArrowUp size={20} />}
        // buttonTitle="Upgrade to View Metrics"
        handleClick={() => { }}
      >
        <div className="flex flex-col justify-center items-center p-4 rounded-lg h-full gap-4">
          {!loading && !error && !noDataAvailable && (
            <div className="flex justify-end w-full max-w-2xl mb-4 gap-4">
              <CustomButton
                className="bg-green-600 hover:bg-green-700 text-white "
                text={<><Printer size={18} />Print</>}
                click={handlePrint}
              >
                Print
              </CustomButton>
              <CustomButton
                className="bg-green-600 hover:bg-green-700 text-white "
                text={<><Download size={18} className="mr-2" />Download</>}
                click={handleDownloadPDF}
              >
              </CustomButton>
            </div>
          )}

          <div ref={reportRef} id="reportRef">
            {loading ? (
              <Typography variant="body1"><Loader /></Typography>
            ) : error ? (
              <Typography variant="body1" color="error">{error}</Typography>
            ) : noDataAvailable ? (
              <div className="text-center py-16 bg-gray-50 rounded-lg w-full max-w-2xl">
                <img
                  src={boiler}
                  alt="No Data"
                  className="w-80 h-80 mx-auto text-gray-400 mb-4"
                />
                <h3 className="text-xl font-medium text-gray-600 mb-2">
                  No Emissions Data Found
                </h3>
                <p className="text-gray-500">
                  Add your first emission source to get started
                </p>
              </div>
            ) : (
              <>
                <ReportingPeriodForm reportData={reportData} />
                <Scope1Form data={reportData?.scope_1} />
                <Scope2Form data={reportData?.scope_2} />
                <Scope3Form data={reportData?.scope_3} />
                <ReportFooter />
              </>
            )}
          </div>
        </div>

        <style jsx global>{`
  @media print {
    body * {
      visibility: hidden;
    }
    #reportRef, #reportRef * {
      visibility: visible;
    }
    #reportRef {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
    }
    .no-print, button {
      display: none !important;
    }
    /* Add these to ensure proper rendering */
    .MuiCard-root {
      break-inside: avoid;
      margin-bottom: 20px;
    }
    .report-footer {
      position: fixed;
      bottom: 20px;
      right: 20px;
      width: 100%;
    }
  }
`}</style>

      </PageHeader2>
    </div>
  );
};

export default GHGMethodologyReport;